import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShieldAlert, ShieldCheck, ClipboardCheck, FileSignature,
  BadgeCheck, Settings, LogOut, Database, GitBranch, Wrench, GaugeCircle,
  CheckSquare, Building2, BookOpen, Activity, Inbox, Map as MapIcon,
} from 'lucide-react';
import { useHGS } from '../context/HGSContext';
import { ROLES } from '../data/mockData';
import { ROLE_NAV_PERMISSIONS } from '../data/rbac';
import { cn } from '@/lib/utils';

const MY_NAV = [
  { to: '/my-tasks', label: 'My Dashboard', icon: CheckSquare },
];

// Operating Head's "My Workspace" nav — OH-scoped views only
const OH_NAV = [
  { to: '/dashboard', label: 'My Dashboard', icon: LayoutDashboard },
  { to: '/risk', label: 'My Risk Evaluation', icon: ClipboardCheck },
];

// Division Head's "My Workspace" nav — review-centric
const DIV_NAV = [
  { to: '/div-dashboard', label: 'My Dashboard', icon: LayoutDashboard },
];

const ARCHITECTURE_NAV = [
  { to: '/risk', label: 'Risk Universe Setup', icon: ShieldAlert, step: 1 },
  { to: '/fraud', label: 'Fraud Prevention', icon: ShieldCheck, step: 2 },
  { to: '/pcc', label: 'Product Compliance (PCC)', icon: ClipboardCheck, step: 3 },
  { to: '/dlc', label: 'DLC Self-Verification', icon: FileSignature, step: 4 },
  { to: '/grc-review', label: 'GRC Review Dashboard', icon: GaugeCircle, step: 5 },
];

// Operating Heads see only downstream modules (no Risk Universe Setup framing,
// no GRC Review enterprise dashboard) — they access /risk via "My Risk Evaluation".
const OH_MODULES_NAV = [
  { to: '/fraud', label: 'Fraud Prevention', icon: ShieldCheck },
  { to: '/pcc', label: 'Product Compliance (PCC)', icon: ClipboardCheck },
  { to: '/dlc', label: 'DLC Self-Verification', icon: FileSignature },
];

const GOVERNANCE_NAV = [
  { to: '/tasks', label: 'Tasks', icon: Inbox },
  { to: '/countermeasures', label: 'Countermeasures', icon: Wrench },
  { to: '/linkage', label: 'Risk-Compliance Linkage', icon: GitBranch },
];

const APPROVALS_NAV = [
  { to: '/approvals', label: 'Approvals', icon: BadgeCheck },
];

const ADMIN_NAV = [
  { to: '/admin/grc-dashboard', label: 'GRC Dashboard', icon: Activity },
  { to: '/admin', label: 'Secretariat Configuration', icon: Settings },
  { to: '/admin/masters', label: 'Master Configuration', icon: Database },
  { to: '/admin/applicability', label: 'Applicability Matrix', icon: MapIcon },
  { to: '/admin/question-bank', label: 'Master Question Bank', icon: BookOpen },
];

export default function Sidebar() {
  const { user, logout, countermeasures } = useHGS();
  const navigate = useNavigate();
  const role = ROLES.find((r) => r.id === user?.role);
  const perms = ROLE_NAV_PERMISSIONS[user?.role] || ROLE_NAV_PERMISSIONS.operating_head;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // For Department Head: only count CMs for their department
  const openCms = (countermeasures || [])
    .filter((c) => c.status !== 'Closed')
    .filter((c) => user?.role !== 'department_head' || c.department === user.department)
    .length;

  const renderItem = ({ to, label, icon: Icon, step, badge }) => (
    <li key={to}>
      <NavLink
        data-testid={`nav-${to.replace(/\//g, '-').replace(/^-/, '')}`}
        to={to}
        className={({ isActive }) =>
          cn(
            'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all relative',
            isActive
              ? 'bg-[#CC0000]/8 text-[#CC0000] shadow-sm'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          )
        }
      >
        {({ isActive }) => (
          <>
            {typeof step === 'number' && (
              <span
                className={cn(
                  'w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                  isActive ? 'bg-[#CC0000] text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                )}
              >
                {step}
              </span>
            )}
            {typeof step !== 'number' && <Icon className={cn('w-[18px] h-[18px]', isActive ? 'text-[#CC0000]' : 'text-slate-400')} />}
            <span className="truncate">{label}</span>
            {badge ? (
              <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                {badge}
              </span>
            ) : null}
          </>
        )}
      </NavLink>
    </li>
  );

  return (
    <aside className="w-64 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0" data-testid="sidebar">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#CC0000] to-[#990000] flex items-center justify-center text-white font-bold text-sm shadow-sm">
            H
          </div>
          <div>
            <div className="text-[15px] font-bold text-slate-900 tracking-tight">HGS</div>
            <div className="text-[10px] text-slate-500 font-medium tracking-wider uppercase">Governance System</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {perms.myTasks && (
          <>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">My Workspace</div>
            <ul className="space-y-0.5 mb-5">
              {MY_NAV.map(renderItem)}
            </ul>
          </>
        )}

        {user?.role === 'operating_head' && (
          <>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">My Workspace</div>
            <ul className="space-y-0.5 mb-5" data-testid="oh-workspace-nav">
              {OH_NAV.map(renderItem)}
            </ul>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Modules</div>
            <ul className="space-y-0.5 mb-5">
              {OH_MODULES_NAV.map(renderItem)}
            </ul>
          </>
        )}

        {user?.role === 'division_head' && (
          <>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">My Workspace</div>
            <ul className="space-y-0.5 mb-5" data-testid="div-workspace-nav">
              {DIV_NAV.map(renderItem)}
            </ul>
          </>
        )}

        {perms.architecture && user?.role !== 'operating_head' && (
          <>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">HGS Architecture</div>
            <ul className="space-y-0.5 mb-5">
              {ARCHITECTURE_NAV.map(renderItem)}
            </ul>
          </>
        )}

        {perms.governance && (
          <>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Governance</div>
            <ul className="space-y-0.5 mb-5">
              {GOVERNANCE_NAV.map((n) => renderItem({ ...n, badge: n.to === '/countermeasures' && openCms > 0 ? openCms : null }))}
            </ul>
          </>
        )}

        {perms.approvals && (
          <>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Review & Approve</div>
            <ul className="space-y-0.5 mb-5">
              {APPROVALS_NAV.map(renderItem)}
            </ul>
          </>
        )}

        {perms.admin && (
          <>
            <div className="px-3 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Admin</div>
            <ul className="space-y-0.5">
              {ADMIN_NAV.map(renderItem)}
            </ul>
          </>
        )}
      </nav>

      <div className="p-3 border-t border-slate-200">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
          <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
            {user?.avatar || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate flex items-center gap-1" title={[role?.label, user?.ohLabel, user?.divisionLabel, user?.departmentLabel].filter(Boolean).join(' › ')}>
              {role?.label}
              {(user?.ohLabel || user?.divisionLabel || user?.departmentLabel) && (
                <>
                  <span>·</span>
                  <Building2 className="w-2.5 h-2.5" />
                  <span className="truncate">
                    {user?.departmentLabel || user?.divisionLabel || user?.ohLabel}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-[#CC0000] transition"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
